import React, { useState, useEffect } from 'react';
import {
  Layout,
  Avatar,
  Upload,
  Button,
  Form,
  Input,
  Select,
  Card,
  Tabs,
  Typography,
  Space,
  message,
  Descriptions,
  Table,
  Modal,
  Row,
  Col,
  Spin,
} from 'antd';
import {
  UploadOutlined,
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  EditOutlined,
  SaveOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/DBContext'; // Adjust the import path as needed

const { Content, Header } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const BusinessProfilePage = () => {
  const {
    currentUser,
    getUserInfo,
    updateBusiness,
    uploadBusinessProfilePicture,
    uploadBusinessDocument,
    fetchServices,
    claimServiceForBusiness,
    fetchClaimedServices,
    sendMessage,
    fetchAdminUsers,
  } = useAuth();

  // State variables
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loadingBusinessInfo, setLoadingBusinessInfo] = useState(true);

  // Separate forms and editing states for each tab
  const [generalForm] = Form.useForm();
  const [bankingForm] = Form.useForm();
  const [isEditingGeneral, setIsEditingGeneral] = useState(false);
  const [isEditingBanking, setIsEditingBanking] = useState(false);

  const [activeTabKey, setActiveTabKey] = useState('1');
  const [claimedServices, setClaimedServices] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [isPictureModalVisible, setIsPictureModalVisible] = useState(false);
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [pictureUploadForm] = Form.useForm();
  const [documentUploadForm] = Form.useForm();
  const [messageForm] = Form.useForm();
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedPictureFile, setSelectedPictureFile] = useState(null);
  const [selectedDocumentFile, setSelectedDocumentFile] = useState(null);
  const [messageContent, setMessageContent] = useState('');

  // Fetch admin users on component mount
  useEffect(() => {
    async function loadAdminUsers() {
      try {
        const admins = await fetchAdminUsers();
        setAdminUsers(admins);
      } catch (error) {
        // console.error('Failed to fetch admin users:', error);
      }
    }
    loadAdminUsers();
  }, [fetchAdminUsers]);

  // Fetch business info on component mount
  useEffect(() => {
    async function loadBusinessInfo() {
      setLoadingBusinessInfo(true);
      try {
        const data = await getUserInfo(currentUser.uid);
        setBusinessInfo(data);
      } catch (error) {
        message.error('Failed to load business information.');
        // console.error('Error:', error);
      } finally {
        setLoadingBusinessInfo(false);
      }
    }
    if (currentUser) {
      loadBusinessInfo();
    }
  }, [currentUser]);

  // Fetch available services and claimed services
  useEffect(() => {
    async function loadServices() {
      setLoadingServices(true);
      try {
        const services = await fetchServices();
        setAvailableServices(services);

        // Fetch claimed services for the business
        const claimed = await fetchClaimedServices(currentUser.uid);
        setClaimedServices(claimed);
      } catch (error) {
        message.error('Failed to load services.');
        // console.error('Error:', error);
      } finally {
        setLoadingServices(false);
      }
    }
    if (currentUser) {
      loadServices();
    }
  }, [currentUser]);

  // Handle form submission for editing general info
  const onFinishGeneral = async (values) => {
    setLoadingAction(true);
    try {
      await updateBusiness(currentUser.uid, values);
      setBusinessInfo({
        ...businessInfo,
        ...values,
      });
      message.success('Business information updated successfully!');
      setIsEditingGeneral(false);
    } catch (error) {
      message.error('Failed to update business information.');
      // console.error('Error:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  // Handle form submission for editing banking info
  const onFinishBanking = async (values) => {
    setLoadingAction(true);
    try {
      await updateBusiness(currentUser.uid, values);
      setBusinessInfo({
        ...businessInfo,
        ...values,
      });
      message.success('Banking information updated successfully!');
      setIsEditingBanking(false);
    } catch (error) {
      message.error('Failed to update banking information.');
      // console.error('Error:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handlePictureUpload = async () => {
    if (!selectedPictureFile) {
      message.error('Please select a profile picture to upload.');
      return;
    }

    setUploadingPicture(true);
    try {
      const imageUrl = await uploadBusinessProfilePicture(currentUser.uid, selectedPictureFile);
      await updateBusiness(currentUser.uid, { image: imageUrl });
      setBusinessInfo({
        ...businessInfo,
        image: imageUrl,
      });
      message.success('Profile picture updated successfully!');
      setIsPictureModalVisible(false);
      setSelectedPictureFile(null);
    } catch (error) {
      message.error('Failed to upload profile picture.');
      // console.error('Error:', error);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!selectedDocumentFile) {
      message.error('Please select a document to upload.');
      return;
    }

    setUploadingDocument(true);
    try {
      const documentUrl = await uploadBusinessDocument(currentUser.uid, selectedDocumentFile);
      const updatedDocuments = [...(businessInfo.documents || []), documentUrl];
      await updateBusiness(currentUser.uid, { documents: updatedDocuments });
      setBusinessInfo({
        ...businessInfo,
        documents: updatedDocuments,
      });
      message.success('Document uploaded successfully!');
      setIsDocumentModalVisible(false);
      setSelectedDocumentFile(null);
    } catch (error) {
      message.error('Failed to upload document.');
      // console.error('Error:', error);
    } finally {
      setUploadingDocument(false);
    }
  };

  // Handle edit button click for General Information
  const handleEditClickGeneral = () => {
    setIsEditingGeneral(true);
    generalForm.setFieldsValue(businessInfo);
  };

  // Handle cancel button click for General Information
  const handleCancelClickGeneral = () => {
    setIsEditingGeneral(false);
  };

  // Handle edit button click for Banking Information
  const handleEditClickBanking = () => {
    setIsEditingBanking(true);
    bankingForm.setFieldsValue(businessInfo);
  };

  // Handle cancel button click for Banking Information
  const handleCancelClickBanking = () => {
    setIsEditingBanking(false);
  };

  // Handle claiming a service
  const handleClaimService = async (service) => {
    try {
      await claimServiceForBusiness(service.id, currentUser.uid);
      message.success(`Successfully claimed "${service.name}". Status: Pending`);
      // Update claimed services
      const claimed = await fetchClaimedServices(currentUser.uid);
      setClaimedServices(claimed);
    } catch (error) {
      message.error('Failed to claim service.');
      // console.error('Error:', error);
    }
  };

  // Columns configuration for Claimed Services Tables
  const claimedServicesColumns = [
    {
      title: 'Service Name',
      dataIndex: 'serviceName',
      key: 'serviceName',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => {
        let color = 'geekblue';
        if (text === 'pending') {
          color = 'orange';
        } else if (text === 'approved') {
          color = 'green';
        } else if (text === 'denied') {
          color = 'red';
        }
        return (
          <Text type={text === 'approved' ? 'success' : text === 'pending' ? 'warning' : 'danger'}>
            {text.charAt(0).toUpperCase() + text.slice(1)}
          </Text>
        );
      },
    },
  ];

  const handleSendMessage = async () => {
    setLoadingAction(true);
    try {
      // console.log('Starting to send messages to admins. Number of admins:', adminUsers.length);
      // console.log('Admin users:', adminUsers);
      
      // Send message to all admin users
      await Promise.all(
        adminUsers.map(async (admin) => {
          // console.log('Sending message to admin:', { id: admin.id, name: admin.name });
          await sendMessage(currentUser.uid, currentUser.name, admin.id, messageContent);
          // console.log('Successfully sent message to admin:', admin.id);
        })
      );
      
      message.success('Message sent to admin successfully!');
      setIsMessageModalVisible(false);
      setMessageContent('');
    } catch (error) {
      message.error('Failed to send message to admin.');
      // console.error('Error in handleSendMessage:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  if (loadingBusinessInfo || loadingServices) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <Spin tip="Loading business information..." size="large" />
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Header with Send Message to Admin Button */}
      <Header
        style={{
          background: 'transparent',
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          boxShadow: '0 2px 8px #f0f1f2',
        }}
        className="responsive-header"
      >
        <Row style={{ width: '100%' }} justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0, lineHeight: '64px' }} className="header-title">
              Business Management
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<MailOutlined />}
              onClick={() => setIsMessageModalVisible(true)}
              className="message-button"
            >
              Send Message to Admin
            </Button>
          </Col>
        </Row>
      </Header>

      {/* Content Area */}
      <Content style={{ margin: '24px 16px 0', overflow: 'initial' }} className="responsive-content">
        <Card className="responsive-card">
          <Row gutter={[16, 16]}>
            {/* Left Column with Avatar and Business Info */}
            <Col xs={24} md={6} style={{ textAlign: 'center' }}>
              <Title level={4} style={{ marginTop: '16px' }} className="business-title">
                {businessInfo.name}
              </Title>
              <Text type="secondary">{businessInfo.typeOfEntity}</Text>
            </Col>

            {/* Right Column with Tabs */}
            <Col xs={24} md={18}>
              <Tabs
                activeKey={activeTabKey}
                onChange={(key) => setActiveTabKey(key)}
                type="card"
                className="responsive-tabs"
              >
                {/* General Information Tab */}
                <TabPane tab="General Information" key="1">
                  {!isEditingGeneral ? (
                    <Card>
                      <Descriptions
                        column={1}
                        bordered
                        size="small"
                        labelStyle={{ width: '200px' }}
                        className="responsive-descriptions"
                      >
                        <Descriptions.Item label="Business Name">
                          {businessInfo.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="Email Address">
                          {businessInfo.email}
                        </Descriptions.Item>
                        <Descriptions.Item label="Business Address">
                          {businessInfo.address}
                        </Descriptions.Item>
                        <Descriptions.Item label="Postcode">
                          {businessInfo.postcode}
                        </Descriptions.Item>
                        <Descriptions.Item label="Type of Entity">
                          {businessInfo.organizationType}
                        </Descriptions.Item>
                        <Descriptions.Item label="Registration Number">
                          {businessInfo.registrationNumber}
                        </Descriptions.Item>
                        <Descriptions.Item label="VAT Number">
                          {businessInfo.vatNumber}
                        </Descriptions.Item>
                        <Descriptions.Item label="Business Role">
                          {businessInfo.businessRole}
                        </Descriptions.Item>
                        {/* <Descriptions.Item label="Local Authority">
                          {businessInfo.localAuthorityName}
                        </Descriptions.Item> */}
                      </Descriptions>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={handleEditClickGeneral}
                        style={{ marginTop: '16px' }}
                      >
                        Edit
                      </Button>
                    </Card>
                  ) : (
                    <Card>
                      <Form
                        form={generalForm}
                        layout="vertical"
                        initialValues={businessInfo}
                        onFinish={onFinishGeneral}
                      >
                        <Form.Item
                          name="name"
                          label="Business Name"
                          rules={[
                            {
                              required: true,
                              message: 'Please input your business name!',
                            },
                          ]}
                        >
                          <Input placeholder="Enter business name" />
                        </Form.Item>
                        <Form.Item
                          name="email"
                          label="Email Address"
                          rules={[
                            {
                              required: true,
                              message: 'Please input your email address!',
                            },
                            { type: 'email', message: 'Please enter a valid email!' },
                          ]}
                        >
                          <Input placeholder="Enter email address" disabled />
                        </Form.Item>
                        <Form.Item
                          name="address"
                          label="Business Address"
                          rules={[
                            {
                              required: true,
                              message: 'Please input your business address!',
                            },
                          ]}
                        >
                          <Input placeholder="Enter business address" />
                        </Form.Item>
                        <Form.Item
                          name="postcode"
                          label="Postcode"
                          rules={[
                            {
                              required: true,
                              message: 'Please input your postcode!',
                            },
                          ]}
                        >
                          <Input placeholder="Enter postcode" />
                        </Form.Item>
                        <Form.Item
                          name="typeOfEntity"
                          label="Type of Entity"
                          rules={[
                            {
                              required: true,
                              message: 'Please select type of entity!',
                            },
                          ]}
                        >
                          <Select>
                            <Option value="Limited Company">Limited Company</Option>
                            <Option value="Charity">Charity</Option>
                            <Option value="Community Interest Company (CIC)">
                              Community Interest Company (CIC)
                            </Option>
                            <Option value="Sole Trader">Sole Trader</Option>
                            <Option value="Limited Liability Partnership">
                              Limited Liability Partnership
                            </Option>
                            <Option value="Other">Other (please state)</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="registrationNumber"
                          label="Registration Number"
                          rules={[
                            {
                              required: true,
                              message: 'Please input your registration number!',
                            },
                          ]}
                        >
                          <Input placeholder="Enter registration number" />
                        </Form.Item>
                        <Form.Item
                          name="vatNumber"
                          label="VAT Number"
                          rules={[
                            {
                              required: true,
                              message: 'Please input your VAT number!',
                            },
                          ]}
                        >
                          <Input placeholder="Enter VAT number" />
                        </Form.Item>
                        <Form.Item
                          name="businessRole"
                          label="Business Role"
                          rules={[
                            {
                              required: true,
                              message: 'Please select your business role!',
                            },
                          ]}
                        >
                          <Select>
                            <Option value="Owner">Owner</Option>
                            <Option value="Manager">Manager</Option>
                            <Option value="Staff">Staff</Option>
                          </Select>
                        </Form.Item>
                        {/* <Form.Item
                          name="localAuthorityName"
                          label="Local Authority"
                        >
                          <Input placeholder="Enter local authority" disabled />
                        </Form.Item> */}
                        <Form.Item>
                          <Space>
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<SaveOutlined />}
                              loading={loadingAction}
                            >
                              Save Changes
                            </Button>
                            <Button onClick={handleCancelClickGeneral}>Cancel</Button>
                          </Space>
                        </Form.Item>
                      </Form>
                    </Card>
                  )}
                </TabPane>

                {/* Banking Information Tab */}
                <TabPane tab="Banking Information" key="2">
                  {!isEditingBanking ? (
                    <Card>
                      <Descriptions
                        column={1}
                        bordered
                        size="small"
                        labelStyle={{ width: '200px' }}
                        className="responsive-descriptions"
                      >
                        <Descriptions.Item label="Bank Account Number">
                          {businessInfo.bankAccountNumber}
                        </Descriptions.Item>
                        <Descriptions.Item label="Sort Code">
                          {businessInfo.sortCode}
                        </Descriptions.Item>
                      </Descriptions>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={handleEditClickBanking}
                        style={{ marginTop: '16px' }}
                      >
                        Edit
                      </Button>
                    </Card>
                  ) : (
                    <Card>
                      <Form
                        form={bankingForm}
                        layout="vertical"
                        initialValues={businessInfo}
                        onFinish={onFinishBanking}
                      >
                        <Form.Item
                          name="bankAccountNumber"
                          label="Bank Account Number"
                          rules={[
                            {
                              required: true,
                              message: 'Please input your bank account number!',
                            },
                          ]}
                        >
                          <Input placeholder="Enter bank account number" />
                        </Form.Item>
                        <Form.Item
                          name="sortCode"
                          label="Sort Code"
                          rules={[
                            {
                              required: true,
                              message: 'Please input your sort code!',
                            },
                          ]}
                        >
                          <Input placeholder="Enter sort code" />
                        </Form.Item>
                        <Form.Item>
                          <Space>
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<SaveOutlined />}
                              loading={loadingAction}
                            >
                              Save Changes
                            </Button>
                            <Button onClick={handleCancelClickBanking}>Cancel</Button>
                          </Space>
                        </Form.Item>
                      </Form>
                    </Card>
                  )}
                </TabPane>

                {/* Documents Tab */}
                <TabPane tab="Documents" key="3">
                  <Card>
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      onClick={() => setIsDocumentModalVisible(true)}
                      style={{ marginBottom: '16px' }}
                    >
                      Upload Document
                    </Button>
                    <Title level={5}>Uploaded Documents</Title>
                    {businessInfo.documents && businessInfo.documents.length > 0 ? (
                      businessInfo.documents.map((doc, index) => (
                        <Card key={index} style={{ marginBottom: '12px' }}>
                          <Space align="center">
                            <FileTextOutlined style={{ fontSize: '24px' }} />
                            <a href={doc} target="_blank" rel="noopener noreferrer">
                              Document {index + 1}
                            </a>
                          </Space>
                        </Card>
                      ))
                    ) : (
                      <Text>No documents uploaded.</Text>
                    )}
                  </Card>
                </TabPane>

                {/* Claim Services Tab */}
                <TabPane tab="Claim Services" key="4">
                  {/* <Card>
                    <Title level={5}>Available Services</Title>
                    {availableServices.length > 0 ? (
                      availableServices.map((service) => (
                        <Card key={service.id} style={{ marginBottom: '12px' }}>
                          <Space align="center">
                            <Avatar src={service.image} size="large" />
                            <div>
                              <Title level={5}>{service.name}</Title>
                              <Text>{service.description}</Text>
                              <Button
                                type="link"
                                onClick={() => handleClaimService(service)}
                                disabled={service.claimed}
                              >
                                {service.claimed ? 'Already Claimed' : 'Claim Service'}
                              </Button>
                            </div>
                          </Space>
                        </Card>
                      ))
                    ) : (
                      <Text>No available services.</Text>
                    )}
                  </Card> */}

                  <Card style={{ marginTop: '0px' }}>
                    <Title level={5}>Pending Approval</Title>
                    <Table
                      columns={claimedServicesColumns}
                      dataSource={claimedServices.filter(
                        (service) => service.status === 'pending'
                      )}
                      rowKey="id"
                      pagination={{ pageSize: 5 }}
                      bordered
                      className="responsive-table"
                    />
                    <Title level={5} style={{ marginTop: '24px' }}>
                      Approved Services
                    </Title>
                    <Table
                      columns={claimedServicesColumns}
                      dataSource={claimedServices.filter(
                        (service) => service.status === 'approved'
                      )}
                      rowKey="id"
                      pagination={{ pageSize: 5 }}
                      bordered
                      className="responsive-table"
                    />
                  </Card>
                </TabPane>
              </Tabs>
            </Col>
          </Row>
        </Card>
      </Content>

      {/* Message Modal */}
      <Modal
        title="Send Message to Admin"
        open={isMessageModalVisible}
        onOk={handleSendMessage}
        onCancel={() => {
          setIsMessageModalVisible(false);
          setMessageContent('');
        }}
        okText="Send"
        confirmLoading={loadingAction}
      >
        <Form layout="vertical">
          <Form.Item label="Message">
            <Input.TextArea 
              rows={4} 
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="Type your message here..."
            />
          </Form.Item>
        </Form>
      </Modal>

    </Layout>
  );
};

// Add this before the export statement
const styles = document.createElement('style');
styles.innerHTML = `
  @media (max-width: 768px) {
    .responsive-header {
      padding: 0 16px !important;
    }
    
    .header-title {
      font-size: 18px !important;
      line-height: 24px !important;
    }
    
    .message-button {
      font-size: 12px !important;
      padding: 0 8px !important;
    }
    
    .message-button span:last-child {
      display: none;
    }
    
    .responsive-content {
      margin: 16px 8px 0 !important;
      padding: 0 8px !important;
    }
    
    .responsive-card .ant-card-body {
      padding: 16px 12px !important;
    }
    
    .business-title {
      font-size: 18px !important;
      margin-top: 8px !important;
    }
    
    .responsive-tabs .ant-tabs-nav {
      margin-bottom: 12px !important;
    }
    
    .responsive-descriptions .ant-descriptions-item-label {
      width: 40% !important;
    }
    
    .responsive-descriptions .ant-descriptions-item-content {
      width: 60% !important;
    }
    
    .responsive-table {
      font-size: 12px !important;
    }
    
    .responsive-table .ant-table-container {
      overflow-x: auto !important;
    }
  }
`;
document.head.appendChild(styles);

export default BusinessProfilePage;
